import { useEffect, useState } from "react";
import type { Kunde, Projekt } from "../../lib/types";
import PackzettelListe from "./PackzettelListe";
import {
  allowedStatuses,
  errorText,
  euro,
  invoiceDokument,
  invoicePdfUrl,
  invoiceSeiteUrl,
  KIND_HINT,
  belegArtLabel,
  KIND_LABEL,
  RECHNUNGS_ARTEN,
  shortDate,
  STATUS_LABEL,
  statusTone,
  type InvoiceKind,
  type InvoiceRegisterItem,
  type InvoiceStatus,
} from "../../lib/invoices";
import { useInvoiceDetail, useRechnungAnDatev, useUpdateInvoiceRecord } from "../../hooks/useInvoices";
import { BelegVersand } from "./BelegVersand";
import BelegNummerKorrektur from "./BelegNummerKorrektur";
import InvoiceFolgebeleg from "./InvoiceFolgebeleg";
import BelegDateiNachreichen from "./BelegDateiNachreichen";
import Combobox, { type ComboboxEintrag } from "../Combobox";
import { useNachrichtenBezug } from "../nachrichten/NachrichtenKontext";
import DokumentLightbox, { type Dokument } from "../DokumentLightbox";

type Props = {
  item: InvoiceRegisterItem;
  customers: Kunde[];
  projects: Projekt[];
  onClose: () => void;
  onUpdated: (item: InvoiceRegisterItem) => void;
};

const kundenEintraege = (customers: Kunde[]): ComboboxEintrag[] =>
  customers.map((customer) => ({ id: customer.id, label: customer.name, zusatz: customer.ort || "" }));

const projektEintraege = (projects: Projekt[]): ComboboxEintrag[] =>
  projects.map((project) => ({
    id: project.id,
    label: project.title,
    zusatz: project.kunde_name || "ohne Kunde",
  }));

/**
 * Das Belegdetail — flach wie der Editor (Redesign 02.09.2026): Kopf mit
 * Status, darunter eine Reihe Aktionen, dann das Dokument, die Summen als
 * Leiste, und erst danach die Metadaten. Wer einen Beleg öffnet, will zuerst
 * sehen, was drinsteht, und dann etwas damit tun.
 */
export default function InvoiceDetailDrawer({ item: zeile, customers, projects, onClose, onUpdated }: Props) {
  // Die Registerzeile kommt ohne Aktivitätsspur und Dokument-JSON (Seitenweise
  // Liste); beides holt das Detail nach. Bis dahin steht die Zeile selbst.
  const detailQuery = useInvoiceDetail(zeile.id);
  const item = detailQuery.data && detailQuery.data.id === zeile.id
    ? { ...zeile, ...detailQuery.data, ...(zeile.updated_at > detailQuery.data.updated_at ? zeile : {}) }
    : zeile;
  const updateRecord = useUpdateInvoiceRecord();
  const [status, setStatus] = useState<InvoiceStatus>(item.status);
  const [kind, setKind] = useState<InvoiceKind>(item.kind);
  const [schliesst, setSchliesst] = useState(Boolean(item.schliesst_auftrag));
  const [dok, setDok] = useState<Dokument>(null);
  const [customerId, setCustomerId] = useState(item.kunde_id);
  const [projectId, setProjectId] = useState(item.projekt_id);
  useNachrichtenBezug({ kundeId: customerId, kundeName: customers.find((c) => c.id === customerId)?.name, projektId: projectId });
  const [dueDate, setDueDate] = useState(item.faellig_am);
  const [note, setNote] = useState(item.notiz);
  const [validation, setValidation] = useState("");

  /**
   * Serverstand in die Felder — aber nur, wenn er sich wirklich geändert hat.
   *
   * `item` wird oben bei **jedem Rendern neu zusammengesetzt** (Registerzeile
   * plus nachgeladenes Detail). Als Abhängigkeit war es deshalb bei jedem
   * Rendern ein anderes Objekt, der Effekt lief jedes Mal — und setzte eine
   * gerade getroffene Auswahl sofort wieder auf den Serverstand zurück. Wer
   * ein Projekt wählte und „Änderungen speichern" drückte, schickte den leeren
   * Wert ab und sah danach wieder kein Projekt (Benjamin, 16.09.2026).
   *
   * Deshalb hängt der Effekt an den **Werten**, nicht an der Identität. Nach
   * dem Speichern ändert `onUpdated` sie, und die Felder ziehen nach.
   */
  const {
    id: standId, status: standStatus, kind: standKind, kunde_id: standKunde,
    projekt_id: standProjekt, faellig_am: standFaellig, notiz: standNotiz,
  } = item;
  const standSchliesst = Boolean(item.schliesst_auftrag);

  useEffect(() => {
    setStatus(standStatus);
    setKind(standKind);
    setSchliesst(standSchliesst);
    setCustomerId(standKunde);
    setProjectId(standProjekt);
    setDueDate(standFaellig);
    setNote(standNotiz || "");
  }, [standId, standStatus, standKind, standKunde, standProjekt, standFaellig, standNotiz, standSchliesst]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const save = async () => {
    setValidation("");
    if (!customerId) return setValidation("Ohne Kunde lässt sich der Beleg nicht speichern.");
    try {
      const updated = await updateRecord.mutateAsync({
        id: item.id,
        patch: {
          status,
          // Angebote bleiben außen vor — der Server weist sie ohnehin ab.
          ...(item.kind === "quote" ? {} : { kind, schliesst_auftrag: kind === "invoice" && schliesst }),
          kunde_id: customerId,
          projekt_id: projectId,
          faellig_am: dueDate,
          notiz: note,
        },
      });
      onUpdated(updated);
    } catch { /* Mutation zeigt die konkrete API-Meldung. */ }
  };

  const activities = [...(item.aktivitaet || [])].reverse();
  const message = validation || (updateRecord.error ? errorText(updateRecord.error) : "");

  return (
    <div className="invoice-drawer-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      {/* Liegt per Portal im body. Ein Klick darin schließt das Belegdetail
          nicht: dessen Hintergrund reagiert nur auf Klicks direkt auf sich. */}
      <DokumentLightbox dok={dok} onClose={() => setDok(null)} />
      <aside className="invoice-drawer" role="dialog" aria-modal="true" aria-labelledby="invoice-detail-title">
        <header className="invoice-drawer-head">
          <div>
            <p className="invoice-kicker">{belegArtLabel(item.kind, item.source_quote_nummer)} <span className={`invoice-pill ${statusTone(item.status)}`}>{STATUS_LABEL[item.status]}</span></p>
            <h2 id="invoice-detail-title">{item.nummer}</h2>
            <p>{item.kunde_name || "Ohne Kunde"}{item.projekt_title ? ` · ${item.projekt_title}` : ""} · {shortDate(item.issue_date)}</p>
          </div>
          <button className="invoice-icon-button" type="button" onClick={onClose} aria-label="Schließen">×</button>
        </header>

        <InvoiceFolgebeleg item={item} onUpdated={onUpdated} />

        <div className="invoice-drawer-scroll">
          {/* Das Dokument selbst gehört nach oben — als Bild der ersten Seite,
              nicht als eingebettetes PDF (das zeigte das Telefon nicht). Ein
              Tipp öffnet alle Seiten in der Dokumentansicht. */}
          {item.web_pdf_available && (
            <button
              type="button"
              className="invoice-drawer-vorschau"
              onClick={() => setDok(invoiceDokument(item, `${item.kunde_name || "Ohne Kunde"} · ${shortDate(item.issue_date)}`))}
            >
              <img src={invoiceSeiteUrl(item, 0, 900)} alt={`Erste Seite von ${item.nummer}`} />
              <span>Ansehen ⤢</span>
            </button>
          )}

          <section className="invoice-detail-sums">
            <div><span>Netto</span><strong>{euro(item.netto)}</strong></div>
            <div><span>USt.</span><strong>{euro(item.mwst)}</strong></div>
            <div className="total"><span>Gesamt</span><strong>{euro(item.brutto)}</strong></div>
          </section>

          {!item.web_pdf_available && <BelegDateiNachreichen item={item} onUpdated={onUpdated} />}

          <VersandBlock item={item} customers={customers} />

          <section className="invoice-detail-section">
            <div className="invoice-section-head">
              <div><p className="invoice-kicker">Bearbeitung</p><h3>Status &amp; Zuordnung</h3></div>
            </div>
            <div className="invoice-status-grid">
              {allowedStatuses(item.kind).map((value) => (
                <button key={value} type="button" className={status === value ? `active ${statusTone(value)}` : ""} onClick={() => setStatus(value)}>
                  {STATUS_LABEL[value]}
                </button>
              ))}
            </div>
            {/* Belegart nachträglich einordnen. Beim Schreiben steht oft noch
                nicht fest, ob eine Rechnung ein Abschlag bleibt oder den Auftrag
                abrechnet — und genau daran hängt der Auftragswert des Projekts.
                Für Angebote gibt es nichts zu wählen: ihr Nummernkreis ist ein
                anderer, aus ihnen wird über „Folgebeleg" eine Rechnung. */}
            {item.kind !== "quote" && (
              <div className="invoice-art-wahl">
                <span className="invoice-kicker">Belegart</span>
                <div className="invoice-status-grid">
                  {RECHNUNGS_ARTEN.map((value) => (
                    <button
                      key={value}
                      type="button"
                      className={kind === value ? "active" : ""}
                      onClick={() => setKind(value)}
                      title={KIND_HINT[value]}
                    >
                      {KIND_LABEL[value]}
                    </button>
                  ))}
                </div>
                <p className="invoice-art-hinweis">{KIND_HINT[kind]}</p>
                {/* Vermerk statt Schlussrechnung (Migration 216): der Rest aus dem
                    Angebot entfällt, das Papier bleibt eine Rechnung. */}
                {kind === "invoice" && (
                  <label className="invoice-editor-check invoice-art-abschluss">
                    <input type="checkbox" checked={schliesst} onChange={(event) => setSchliesst(event.target.checked)} />
                    <span>
                      <strong>Schließt den Auftrag ab</strong>
                      <small>Ein Rest aus dem Angebot wird nicht mehr abgerechnet — der Auftragswert sinkt auf die Summe der Rechnungen.</small>
                    </span>
                  </label>
                )}
              </div>
            )}
            <div className="invoice-form-grid compact">
              {/* Beide Felder waren `datalist` und damit praktisch nicht
                  bedienbar: der Browser filtert auf den ganzen Optionstext,
                  ein Teiltreffer lieferte keine ID, und das Speichern
                  scheiterte an der eigenen Prüfung. */}
              <div className="invoice-field invoice-field-wide">
                <span>Kunde</span>
                <Combobox
                  wert={customerId}
                  eintraege={kundenEintraege(customers)}
                  onWaehlen={setCustomerId}
                  platzhalter="Kunde suchen …"
                  ariaLabel="Kunde des Belegs"
                />
              </div>
              <div className="invoice-field invoice-field-wide">
                <span>Projekt</span>
                <Combobox
                  wert={projectId}
                  eintraege={projektEintraege(projects)}
                  onWaehlen={setProjectId}
                  platzhalter="Projekt suchen …"
                  leerLabel="Ohne Projekt"
                  ariaLabel="Projekt des Belegs"
                />
              </div>
              <label className="invoice-field invoice-field-wide">
                <span>{item.kind === "quote" ? "Gültig bis" : "Fällig am"}</span>
                <input type="date" value={dueDate} onChange={(event) => setDueDate(event.target.value)} />
              </label>
              <label className="invoice-field invoice-field-wide">
                <span>Interne Notiz</span>
                <textarea rows={3} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Noch keine Notiz" />
              </label>
            </div>
            {message && <p className="invoice-form-error" role="alert">{message}</p>}
            <button className="invoice-button primary invoice-save-button" type="button" onClick={save} disabled={updateRecord.isPending}>
              {updateRecord.isPending ? "Wird gespeichert …" : "Änderungen speichern"}
            </button>
          </section>

          <section className="invoice-detail-section">
            <div className="invoice-section-head"><div><p className="invoice-kicker">Dokument</p><h3>Belegdaten</h3></div></div>
            <dl className="invoice-detail-list">
              <div><dt>Belegdatum</dt><dd>{shortDate(item.issue_date)}</dd></div>
              <div><dt>{item.kind === "quote" ? "Gültig bis" : "Fällig"}</dt><dd>{shortDate(item.faellig_am)}</dd></div>
              <div><dt>Bezahlt am</dt><dd>{shortDate(item.bezahlt_am)}</dd></div>
              <div><dt>Quelldokument</dt><dd>{item.yaml_pfad ? "Desktop-Dokument" : "Registereintrag"}</dd></div>
              <div><dt>PDF</dt><dd>{item.pdf_pfad || item.web_pdf_available ? "Vorhanden" : "Noch nicht hinterlegt"}</dd></div>
              <div><dt>ZUGFeRD / XML</dt><dd>{item.xml_pfad || item.web_xml_pfad ? "Vorhanden" : "—"}</dd></div>
            </dl>
            {item.web_pdf_available && <a className="invoice-pdf-link" href={invoicePdfUrl(item)} target="_blank" rel="noreferrer">PDF aus der Web-App öffnen ↗</a>}
            <BelegNummerKorrektur item={item} onUpdated={onUpdated} />
          </section>

          {item.kontobewegung_id && (
            <section className="invoice-detail-section invoice-bank-match">
              <div className="invoice-section-head"><div><p className="invoice-kicker">Zahlung</p><h3>Bankzuordnung</h3></div><span className="invoice-pill success">Zugeordnet</span></div>
              <strong>{euro(item.kb_betrag)}</strong>
              <p>{shortDate(item.kb_datum)} · {item.kb_gegenpartei || "Gegenpartei unbekannt"}</p>
            </section>
          )}

          {/* Die Marge steht nur hier — auf dem Packzettel selbst taucht
              weder sie noch unser Angebotspreis auf. */}
          <PackzettelListe rechnungId={item.id} />

          <section className="invoice-detail-section">
            <div className="invoice-section-head"><div><p className="invoice-kicker">Verlauf</p><h3>Aktivität</h3></div></div>
            <div className="invoice-timeline">
              {activities.map((activity, index) => (
                <div className="invoice-timeline-entry" key={`${activity.ts || "event"}-${index}`}>
                  <span />
                  <div><strong>{activity.text || "Änderung"}</strong><small>{shortDate(activity.ts)}{activity.autor ? ` · ${activity.autor}` : ""}</small></div>
                </div>
              ))}
              {activities.length === 0 && <p className="invoice-empty-copy">Noch keine Aktivitäten protokolliert.</p>}
            </div>
          </section>
        </div>
      </aside>
    </div>
  );
}

/**
 * Der Weg zum Steuerberater — ein Knopf, keine Automatik.
 *
 * Eingangsbelege gehen beim Abschliessen von selbst an das DATEV-Postfach;
 * eine Ausgangsrechnung nicht. Sie entsteht oft in mehreren Anläufen, und eine
 * verschickte Mail holt man nicht zurück — deshalb drückt Benjamin selbst
 * (16.09.2026). Angebote haben hier nichts zu suchen: sie sind kein
 * Buchungsbeleg.
 */
function DatevZeile({ item }: { item: InvoiceRegisterItem }) {
  const senden = useRechnungAnDatev();
  if (item.kind === "quote") return null;
  const versendet = item.an_steuerberater_versendet_at;
  return (
    <>
      <div className="invoice-datev-zeile">
        <span className="invoice-datev-wer">
          <strong>An den Steuerberater</strong>
          <small>
            {versendet
              ? `DATEV · ${shortDate(versendet)}`
              : item.web_pdf_available
                ? "DATEV · noch nicht gesendet"
                : "DATEV · erst festschreiben"}
          </small>
        </span>
        {versendet ? (
          <span className="invoice-pill success">Gesendet</span>
        ) : (
          <button
            className="invoice-button"
            type="button"
            onClick={() => senden.mutate(item.id)}
            disabled={senden.isPending || !item.web_pdf_available}
          >
            {senden.isPending ? "Sendet …" : "An DATEV"}
          </button>
        )}
      </div>
      {senden.isError && <p className="invoice-inline-error">{errorText(senden.error)}</p>}
    </>
  );
}

/**
 * Versand im Beleg-Drawer.
 *
 * Bis hierher fragte der Drawer nur nach dem Empfänger und schickte den
 * Kurztext des Servers — der Editor dagegen ein ausformuliertes Anschreiben.
 * Denselben Beleg zweimal unterschiedlich zu verschicken, je nachdem von wo,
 * war der Fehler; das Formular kommt jetzt aus `BelegVersand`.
 */
function VersandBlock({ item, customers }: { item: InvoiceRegisterItem; customers: Kunde[] }) {
  const kunde = customers.find((c) => c.id === item.kunde_id);
  const art = item.kind === "quote" ? belegArtLabel(item.kind, item.source_quote_nummer) : "Rechnung";
  return (
    <section className="invoice-detail-section">
      <div className="invoice-section-head">
        <div><p className="invoice-kicker">Versand</p><h3>{art} senden</h3></div>
      </div>
      <BelegVersand
        item={item}
        kundeEmail={kunde?.email || ""}
        kundeKontakteJson={kunde?.kontakte_json || ""}
        kundeName={kunde?.name || item.kunde_name}
      kunde={kunde}
        anstoss="✉ An den Kunden"
      />
      {/* Ein Kasten statt zwei (Benjamin, 17.09.2026): beide Wege schicken
          denselben Beleg weg, nur an verschiedene Empfänger. Zwei Karten mit je
          einer Überschrift und einem Knopf haben dafür ein Drittel des Drawers
          gebraucht. */}
      <DatevZeile item={item} />
    </section>
  );
}
